import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalTitle, 
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Select,
  Alert
} from '../../styles/components';
import { SecretDisplayModal } from '../common/SecretDisplayModal';
import { useCreateManagementClientMutation } from '../../store/api/managementClientsApi';
import { useGetBusinessSystemsQuery } from '../../store/api/businessSystemsApi';
import { useNotifications } from '../../hooks/useNotifications';
import { 
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface CreateManClientWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ManClientData {
  clientId: string;
  organizationName: string;
  organizationId: string;
  businessSystem: string;
}

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  
  > * {
    flex: 1;
  }
`;

const ClientIdRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
  
  input {
    flex: 0 1 auto;
    max-width: 48%;
    
  }
  
  button {
    flex: none;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
`;

const NumberInput = styled(Input)`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const RegenButton = styled(Button)`
  min-width: 2.5rem !important;
  width: 2.5rem !important;
  max-width: 2.5rem !important;
  height: 2.5rem;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const generateClientId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'MANOP';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export function CreateManClientWizard({ onClose, onSuccess }: CreateManClientWizardProps) {
  const [formData, setFormData] = useState<ManClientData>({
    clientId: generateClientId(),
    organizationName: '',
    organizationId: '',
    businessSystem: ''
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState('');
  const [wizardClosed, setWizardClosed] = useState(false);

  const { data: businessSystems } = useGetBusinessSystemsQuery();
  const [createManClient, { isLoading }] = useCreateManagementClientMutation();
  const { notify } = useNotifications();

  const handleInputChange = (field: keyof ManClientData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleRegenerateClientId = () => {
    setFormData(prev => ({ ...prev, clientId: generateClientId() }));
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.clientId.trim()) {
      newErrors.push('Client ID is required');
    }

    if (!formData.organizationName.trim()) {
      newErrors.push('Organization name is required');
    }

    if (!formData.organizationId.trim()) {
      newErrors.push('Organization ID is required');
    }

    if (!formData.businessSystem) {
      newErrors.push('Business system is required');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const result = await createManClient({
        clientId: formData.clientId,
        organizationName: formData.organizationName,
        organizationId: formData.organizationId,
        businessSystem: formData.businessSystem
      }).unwrap();

      // Check if the response contains a secret
      if (result.client?.secret) {
        setGeneratedSecret(result.client.secret);
        setShowSecretModal(true);
        setWizardClosed(true); // Mark wizard as closed
      } else {
        notify.success('Management client created successfully');
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage = error?.data?.errors?.[0] || 'Failed to create management client';
      setErrors([errorMessage]);
    }
  };

  const handleSecretComplete = () => {
    setShowSecretModal(false);
    setGeneratedSecret(''); // Clear secret from memory for security
    notify.success('Management client created successfully');
    onSuccess(); // This will close the wizard and refresh the list
  };

  return (
    <>
      <Modal $isOpen={!wizardClosed}>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Create Management Client</ModalTitle>
            <Button variant="outline" size="sm" onClick={onClose}>
              <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
            </Button>
          </ModalHeader>

          <ModalBody>
            {errors.length > 0 && (
              <Alert variant="error" style={{ marginBottom: '1rem' }}>
                <ExclamationTriangleIcon />
                <div>
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              </Alert>
            )}

            <FormGroup>
              <Label>Client ID</Label>
              <ClientIdRow>
                <Input
                  value={formData.clientId}
                  onChange={(e) => handleInputChange('clientId', e.target.value)}
                  placeholder="Auto-generated client ID"
                />
                <RegenButton 
                  variant="secondary" 
                  onClick={handleRegenerateClientId}
                  type="button"
                  title="Regenerate Client ID"
                >
                  <ArrowPathIcon />
                </RegenButton>
              </ClientIdRow>
            </FormGroup>

            <FormGroup>
              <FormRow>
                <div>
                  <Label>Organization Name</Label>
                  <Input
                    value={formData.organizationName}
                    onChange={(e) => handleInputChange('organizationName', e.target.value)}
                    placeholder="Enter organization name"
                  />
                </div>
                <div>
                  <Label>Organization ID</Label>
                  <Input
                    value={formData.organizationId}
                    onChange={(e) => handleInputChange('organizationId', e.target.value)}
                    placeholder="Enter organization ID"
                  />
                </div>
              </FormRow>
            </FormGroup>

            <FormGroup>
              <Label>Business System *</Label>
              <Select
                value={formData.businessSystem}
                onChange={(e) => handleInputChange('businessSystem', e.target.value)}
              >
                <option value="">Select business system</option>
                {(businessSystems || []).map((bs) => (
                  <option key={bs.id} value={bs.name}>
                    {bs.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Management Client'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SecretDisplayModal
        isOpen={showSecretModal}
        secret={generatedSecret}
        title="Management Client Secret Generated"
        clientId={formData.clientId}
        secretType="client-secret"
        onComplete={handleSecretComplete}
      />
    </>
  );
}